import { Plus } from 'lucide-react';
import React, { FC, useMemo, useRef, useState } from 'react';

import { type Counterparty } from '@column/api';
import { Button, Popover, PopoverContent, PopoverTrigger, cn } from '@column/ui';

import { ModalCounterparty } from '~/components/Dialog';
import { useCounterpartyOptions } from '~/components/fields/CounterpartyDropdown';
import { type HighlightSuffixCandidate, getHighlightedSuffix } from '~/lib/search/highlight';

import {
  type AccountCardRow,
  EmptyAccountTrigger,
  EntitySearchCommand,
  type EntitySearchOption,
  FadeSwap,
  SelectedAccountCard,
  highlightMatch,
  renderMatchFragment,
} from './AccountSelector';
import { FADE_ONLY_MOTION, OVERLAP_CELL, OVERLAP_GRID, POPOVER_CONTENT_CLASS, TRIGGER_HEIGHT_PX } from './constants';

// Fields not otherwise shown in a row; when the search matched one, it's revealed in place of the account line.
const HIDDEN_MATCH_CANDIDATES: HighlightSuffixCandidate[] = [
  { field: 'BENEFICIARY_EMAIL', label: 'EMAIL' },
  { field: 'BENEFICIARY_PHONE', label: 'PHONE' },
  { field: 'LOCAL_ACCOUNT_NUMBER', label: 'LOCAL ACCT' },
  { field: 'LOCAL_BANK_CODE', label: 'LOCAL BANK' },
  { field: 'COUNTERPARTY_ID', label: 'ID' },
];

export interface CounterpartySelectorProps {
  value: string;
  onChange: (id: string) => void;
  onBlur: () => void;
  hasError?: boolean;
  counterpartyType: string;
  dataCy?: string;
}

export const CounterpartySelector: FC<CounterpartySelectorProps> = ({
  value,
  onChange,
  onBlur,
  hasError,
  counterpartyType,
  dataCy,
}) => {
  const [open, setOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selectedOnClose = useRef(false);
  const { counterparties, activeCounterparty, activeCounterpartyIsLoading, search, handleScrollEnd } =
    useCounterpartyOptions({ value });

  const options: EntitySearchOption[] = useMemo(
    () =>
      counterparties.map((cp) => {
        const matchedHidden = getHighlightedSuffix(cp.search_metadata, HIDDEN_MATCH_CANDIDATES);
        return {
          id: cp.id,
          primary: highlightMatch(cp.name || cp.description || cp.wire?.beneficiary_name || 'Unnamed', query),
          secondary: matchedHidden ? (
            <span className="font-mono">
              <span className="text-muted-foreground/60">{matchedHidden.label} </span>
              {renderMatchFragment(matchedHidden.fragment)}
            </span>
          ) : cp.account_number || cp.routing_number ? (
            <span className="font-mono">
              {cp.account_number && (
                <>
                  <span className="text-muted-foreground/60">ACCT </span>
                  {highlightMatch(cp.account_number, query)}
                </>
              )}
              {cp.account_number && cp.routing_number && <span className="text-muted-foreground/60"> / </span>}
              {cp.routing_number && (
                <>
                  <span className="text-muted-foreground/60">RTN </span>
                  {highlightMatch(cp.routing_number, query)}
                </>
              )}
            </span>
          ) : undefined,
        };
      }),
    [counterparties, query]
  );

  // Fall back to the list entry so a failed or forbidden detail fetch never blanks the card.
  const counterparty = (activeCounterparty ?? counterparties.find((cp) => cp.id === value)) as Counterparty | undefined;
  const rows: AccountCardRow[] = counterparty
    ? [
        { label: 'Counterparty ID', value: counterparty.id || '—', monospaced: true },
        { label: 'Account number', value: counterparty.account_number || '—', monospaced: true },
        { label: 'Routing number', value: counterparty.routing_number || '—', monospaced: true },
      ]
    : [];

  const handleCreated = (created: Counterparty) => {
    setIsModalOpen(false);
    setOpen(false);
    onChange(created.id);
  };

  return (
    <>
      <div className={OVERLAP_GRID}>
        <SelectedAccountCard
          className={OVERLAP_CELL}
          open={Boolean(value)}
          title={counterparty?.name || counterparty?.description || 'Counterparty'}
          rows={rows}
          isLoading={!counterparty && activeCounterpartyIsLoading}
          hasError={hasError}
          onClear={() => onChange('')}
        />
        <FadeSwap visible={!value} className={OVERLAP_CELL}>
          <div data-cy={dataCy}>
            <Popover
              open={open}
              onOpenChange={(next) => {
                setOpen(next);
                if (!next) {
                  onBlur();
                  setQuery('');
                  search('');
                }
              }}
            >
              {/* A sibling rather than a child of the trigger, since buttons can't nest. */}
              <div className="relative">
                <PopoverTrigger asChild>
                  <EmptyAccountTrigger label="Select a counterparty" hasError={hasError} className="[&>span]:pr-20" />
                </PopoverTrigger>
                <Button
                  variant="muted"
                  size="xs"
                  type="button"
                  onClick={() => setIsModalOpen(true)}
                  className={cn(
                    'absolute top-1/2 right-10 -translate-y-1/2 transition-opacity',
                    open && 'pointer-events-none opacity-0'
                  )}
                >
                  <Plus />
                  Create
                </Button>
              </div>
              <PopoverContent
                align="start"
                sideOffset={-TRIGGER_HEIGHT_PX}
                className={POPOVER_CONTENT_CLASS}
                {...FADE_ONLY_MOTION}
                onCloseAutoFocus={(event) => {
                  // After a selection the trigger is hidden, so don't restore focus to it.
                  if (selectedOnClose.current) {
                    selectedOnClose.current = false;
                    event.preventDefault();
                  }
                }}
              >
                <EntitySearchCommand
                  options={options}
                  searchPlaceholder="Search counterparties…"
                  emptyMessage="No counterparties found"
                  onSearch={(q) => {
                    setQuery(q);
                    search(q);
                  }}
                  onSelect={(id) => {
                    selectedOnClose.current = true;
                    onChange(id);
                    setOpen(false);
                    setQuery('');
                    search('');
                  }}
                  onScrollEnd={handleScrollEnd}
                  footer={
                    <div className="border-t border-gray-100 p-2 dark:border-white/[0.08]">
                      <Button
                        variant="secondary"
                        size="sm"
                        type="button"
                        className="w-full"
                        onClick={() => {
                          setOpen(false);
                          setQuery('');
                          search('');
                          setIsModalOpen(true);
                        }}
                      >
                        <Plus />
                        Create Counterparty
                      </Button>
                    </div>
                  }
                />
              </PopoverContent>
            </Popover>
          </div>
        </FadeSwap>
      </div>
      <ModalCounterparty
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        callback={handleCreated}
        type={counterpartyType}
      />
    </>
  );
};
